import CoreProTable, { CoreProTableState } from '../../core/components/CoreProTable';
import { AbandonWallet, AbandonWalletProvider } from '../../domain/usdt-providers/AbandonWalletProvider';
import React, { useEffect, useRef, useState } from 'react';
import { ProCoreActionType } from '@ant-design/pro-utils';
import { FormInstance } from 'antd/lib/form/hooks/useForm';
import { useTranslation } from 'react-i18next';
import { usePageState } from '../../core/hooks/usePageState';
import { Button, PageHeader, Space, Tag, Tooltip, message } from 'antd';
import PaginationTransformer from '../../core/utils/paginationTransformer';
import SystemIdSearchList from '../../domain/components/system-search-list/SystemIdSearchList';
import { ChannelValueEnum, Token } from '../../domain/channel/channel';
import TransferForm, { TransferFormProps, TransferOrderClosedEvent } from '../component/TransferForm';
import { EventProvider } from '../../../event/eventProvider';
import { filter } from 'rxjs/operators';
import HexLabel from '../../core/components/HexLabel';
import { transfer2LocalTime } from '../../core/utils/timeFormat';
import { useOperatorLogger } from '../../core/hooks/useOperatorLogger';
import { WRITE_ABANDON_WALLET } from '../configs/scopes';
import _module from '../configs/module';
import {
  AbandonMerchantModal,
  AbandonWalletsModal,
  RetireConfirmModal,
} from '../component/RetireActions';

const defailtTransferState: TransferFormProps = {
  systemId: '',
  visible: false,
  balance: '0',
  channel: '',
  token: '',
  walletAddress: '',
};

interface PageOp extends CoreProTableState<AbandonWallet> {}

export default function AbandonWalletPage(props: {}) {
  const actionRef = useRef<ProCoreActionType>();
  const searchFormRef = useRef<FormInstance>();
  // 掃描後自動輪詢刷新的計時器,離開頁面時要清掉。
  const scanPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const { t } = useTranslation();
  const pageOp = usePageState<PageOp>('PAGE_REMEMBER', {
    params: {},
    collapsed: false,
    pagination: {
      pageSize: 10,
      current: 1,
    },
    needRequest: true,
  });
  const [transferState, setTransferState] = useState<TransferFormProps>(
    defailtTransferState,
  );
  const loggger = useOperatorLogger();

  // 勾選的是「列」(每幣一列),但清空是以錢包為單位 —— 送出前去重,
  // 否則同一顆錢包的 USDT/TRX 兩列會讓數字看起來是兩顆。
  const [selected, setSelected] = useState<AbandonWallet[]>([]);
  const [abandonOpen, setAbandonOpen] = useState(false);
  const [walletsOpen, setWalletsOpen] = useState(false);
  const [confirm, setConfirm] = useState<{
    open: boolean;
    action: 'sweep' | 'unabandon';
  }>({ open: false, action: 'sweep' });
  const selectedAddresses = Array.from(new Set(selected.map((r) => r.walletAddress)));
  const canWrite = _module.can([WRITE_ABANDON_WALLET]);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const checkTransfer = (row: AbandonWallet) => {
    return row.txnBalance > 0;
  };

  const clearAndRefresh = () => {
    setSelected([]);
    refresh();
  };

  const doScan = async () => {
    if (!selectedAddresses.length) return;
    try {
      const r = await AbandonWalletProvider.scanChain(selectedAddresses);
      message.success(
        `已排入掃描佇列(${r.queued} 列)—— 背景掃描中,鏈上餘額會自動更新,不必手動刷新`,
      );
      clearAndRefresh();
      // 掃描是背景 cron(每分鐘跑),剛排入時鏈上餘額還沒回填 —— 自動輪詢刷新幾輪,
      // 讓鏈上餘額出現後畫面自動更新,免得使用者以為「掃了沒作用」。
      if (scanPollRef.current) clearInterval(scanPollRef.current);
      let ticks = 0;
      scanPollRef.current = setInterval(() => {
        ticks += 1;
        refresh();
        if (ticks >= 8 && scanPollRef.current) {
          clearInterval(scanPollRef.current); // ~8 × 10s ≈ 80s,涵蓋背景 cron 一輪
          scanPollRef.current = null;
        }
      }, 10000);
    } catch (e: any) {
      message.error(
        e?.response?.data?.displayMessage || e?.response?.data?.message || '掃描排入失敗',
      );
    }
  };

  const refresh = () => {
    pageOp.update((v) => {
      v.needRequest = true;
      return v;
    });
    actionRef.current?.reload();
  };

  useEffect(() => {
    searchFormRef.current?.setFieldsValue(pageOp.get().params);
  }, [searchFormRef, pageOp]);

  useEffect(() => {
    EventProvider.subject
      .pipe(filter((x) => x.eventSymbol === TransferOrderClosedEvent.symbol))
      .subscribe((x) => {
        setTransferState(defailtTransferState);
      });
    EventProvider.subject
      .pipe(filter((x) => x.eventSymbol === TransferOrderClosedEvent.symbol))
      .subscribe((x) => {
        refresh();
      });
    // 離開頁面時停掉掃描輪詢計時器。
    return () => {
      if (scanPollRef.current) {
        clearInterval(scanPollRef.current);
        scanPollRef.current = null;
      }
    };
    // eslint-disable-next-line
  }, []);

  return (
    <>
      <PageHeader
        title={t('abandon_wallet.abandon_wallet')}
        subTitle={''}
        extra={
          <Space>
            {/* 棄用入口已停用(2026-08-05)—— 改用錢包頁「停用」+ 出款錢包「可歸集」旗標控管
                資金流。棄用表/掃描/清空/撤回等既有邏輯全保留,只拿掉這兩個新增入口。
            <Button
              type="primary"
              danger
              disabled={!canWrite}
              onClick={() => setAbandonOpen(true)}
            >
              棄用整個商戶
            </Button>
            <Button disabled={!canWrite} onClick={() => setWalletsOpen(true)}>
              單獨棄用錢包
            </Button>
            */}
            <Tooltip title="只讀鏈,不動錢。掃完列表才看得到每顆的真實餘額">
              <Button disabled={!canWrite || !selectedAddresses.length} onClick={doScan}>
                掃描鏈上餘額({selectedAddresses.length})
              </Button>
            </Tooltip>
            <Button
              danger
              disabled={!canWrite || !selectedAddresses.length}
              onClick={() => setConfirm({ open: true, action: 'sweep' })}
            >
              清空鏈上餘額({selectedAddresses.length})
            </Button>
            <Button
              disabled={!canWrite || !selectedAddresses.length}
              onClick={() => setConfirm({ open: true, action: 'unabandon' })}
            >
              移出棄用
            </Button>
          </Space>
        }
      />
      <CoreProTable<AbandonWallet>
        bordered
        rowKey={(row) =>
          `${row.systemId}:${row.channel}:${row.token}:${row.walletAddress}`
        }
        updateTableState={(state) =>
          pageOp.update((v: any) => ({ ...v, ...state }))
        }
        onReset={() => {
          pageOp.update((v) => {
            v.needRequest = false;
            v.params = {};
            return v;
          });
        }}
        rowSelection={{
          selectedRowKeys: selected.map(
            (r) => `${r.systemId}:${r.channel}:${r.token}:${r.walletAddress}`,
          ),
          onChange: (_keys: any, rows: AbandonWallet[]) => setSelected(rows),
        }}
        scroll={{ x: 'fixed' }}
        tableState={pageOp.get()}
        requestData={(params) =>
          AbandonWalletProvider.getList(
            PaginationTransformer.paramTransfer(params),
          ).then((rs) => {
            loggger.log({
              action: '查詢',
              payload: { req: params },
              systemId: params.systemId
            });
            return PaginationTransformer.responseTransfer(rs);
          })
        }
        actionRef={actionRef as any}
        formRef={searchFormRef}
        columns={[
          {
            fixed: 'left',
            hideInSearch: false,
            width: 100,
            align: 'center',
            title: '商戶名稱',
            dataIndex: 'systemId',
            renderFormItem: (_: any, row, form: FormInstance<any>) => (
              <SystemIdSearchList
                onChange={(value) =>
                  form.setFieldsValue({ [_.dataIndex as string]: value })
                }
                systemId={pageOp.get().params.systemId}
              />
            ),
          },
          {
            align: 'center',
            title: t('payment.channel'),
            dataIndex: 'channel',
            valueType: 'select',
            valueEnum: ChannelValueEnum,
            onFilter: true,
            width: 70,
          },
          {
            align: 'center',
            title: t('payment.token'),
            dataIndex: 'token',
            valueType: 'select',
            valueEnum: Token,
            onFilter: true,
            width: 70,
          },
          {
            hideInSearch: false,
            width: 100,
            align: 'center',
            title: t('payment.wallet_address'),
            ellipsis: true,
            dataIndex: 'walletAddress',
            render: (node, row) => (
              <HexLabel hex={row.walletAddress} size={'small'} />
            ),
          },
          {
            hideInSearch: true,
            width: 80,
            align: 'right',
            title: t('payment.txnBalance'),
            dataIndex: 'txnBalance',
          },
          {
            hideInSearch: true,
            width: 80,
            align: 'right',
            title: t('payment.availableBalance'),
            dataIndex: 'availableBalance',
          },
          {
            hideInSearch: true,
            width: 90,
            align: 'right',
            title: '鏈上餘額',
            dataIndex: 'chainBalance',
            // null 不是 0。沒掃過、或掃了查不到,都顯示「—」—— 顯示成 0 會讓人
            // 以為這顆沒東西可清,而它可能躺著幾百 U。
            render: (node, row) =>
              row.chainBalance === null || row.chainBalance === undefined ? (
                <Tooltip title="還沒掃描,或查鏈失敗 —— 不等於餘額 0">
                  <span style={{ color: '#999' }}>—</span>
                </Tooltip>
              ) : (
                <b>{Number(row.chainBalance).toLocaleString()}</b>
              ),
          },
          {
            hideInSearch: false,
            width: 90,
            align: 'center',
            title: '清空狀態',
            dataIndex: 'sweepStatus',
            valueType: 'select',
            // 'none' 是後端約定的哨兵:欄位 NULL = 從沒排進清空。
            valueEnum: {
              none: { text: '未排清空' },
              pending: { text: '排隊中' },
              running: { text: '清空中' },
              done: { text: '已清空' },
              skipped: { text: '跳過(餘額0)' },
              failed: { text: '失敗' },
            },
            render: (node, row) => {
              if (!row.sweepStatus) return <span style={{ color: '#999' }}>—</span>;
              const map: Record<string, { color: string; text: string }> = {
                pending: { color: 'default', text: '排隊中' },
                running: { color: 'processing', text: '清空中' },
                done: { color: 'success', text: '已清空' },
                skipped: { color: 'default', text: '跳過(餘額0)' },
                failed: { color: 'error', text: '失敗' },
              };
              const m = map[row.sweepStatus] ?? { color: 'default', text: row.sweepStatus };
              return (
                <Tooltip title={row.sweepError || row.sweepTxId || ''}>
                  <Tag color={m.color}>{m.text}</Tag>
                </Tooltip>
              );
            },
          },
          {
            hideInSearch: true,
            width: 80,
            align: 'center',
            title: t('common.createdAt'),
            dataIndex: 'createdAt',
            render: (text, row: any) =>
              transfer2LocalTime(new Date(row.createdAt)),
          },
          // 單筆「下發」已停用(2026-08-12)—— 棄用錢包的出金一律走上方「清空鏈上餘額」,
          // 目的地由後端 RETIRE_SWEEP_DEST 決定,不開放前端指定。端點
          // (/admin/abandon/wallet/transfer)與 TransferForm 都保留,只拿掉入口;
          // 整個「操作」欄一起收起來,免得留一條空白欄。
          // {
          //   title: t('common.operate'),
          //   key: 'actions',
          //   width: 80,
          //   valueType: 'option',
          //   fixed: 'right',
          //   align: 'center',
          //   render: (dom, row) => (
          //     <Space>
          //       {
          //         <Button
          //           key={1}
          //           type="primary"
          //           size={'small'}
          //           disabled={!checkTransfer(row) || !_module.can([WRITE_ABANDON_WALLET])}
          //           onClick={() => {
          //             setTransferState({
          //               visible: true,
          //               balance: row.txnBalance.toString(),
          //               channel: row.channel,
          //               token: row.token,
          //               walletAddress: row.walletAddress,
          //               systemId: row.systemId,
          //             });
          //           }}
          //         >
          //           下發
          //         </Button>
          //       }
          //     </Space>
          //   ),
          // },
        ]}
      />
      <TransferForm {...transferState} />
      <AbandonMerchantModal
        open={abandonOpen}
        onClose={() => setAbandonOpen(false)}
        onDone={clearAndRefresh}
      />
      <AbandonWalletsModal
        open={walletsOpen}
        onClose={() => setWalletsOpen(false)}
        onDone={clearAndRefresh}
      />
      <RetireConfirmModal
        open={confirm.open}
        action={confirm.action}
        addresses={selectedAddresses}
        onClose={() => setConfirm({ ...confirm, open: false })}
        onDone={clearAndRefresh}
      />
    </>
  );
}
